import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';

import NodeConfigPanel from 'features/agents/WorkflowEditor/NodeConfigPanel';
import VersionHistoryPanel from 'features/agents/WorkflowEditor/VersionHistoryPanel';
import NodePicker from 'features/agents/WorkflowEditor/NodePicker';
import EditorTopBar from 'features/agents/WorkflowEditor/EditorTopBar';
import useAgentEditor from 'features/agents/WorkflowEditor/useAgentEditor';
import useRunPolling from 'features/agents/WorkflowEditor/useRunPolling';
import { NODE_CATALOG } from 'features/agents/WorkflowEditor/nodeCatalog';
import FlowCanvas from 'features/agents/canvas/FlowCanvas';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import BrandLoadingSpinner from 'shared/components/ui/BrandLoadingSpinner';

/**
 * The agent editor page: a top bar, the canvas, and the panels that sit
 * beside it (a step's settings, or version history). The document and its
 * saving live in useAgentEditor, test runs in useRunPolling, the picture in
 * FlowCanvas; this file only puts them together.
 *
 * Settings open in a panel beside the canvas and the canvas shrinks to make
 * room; the graph re-fits in what is left.
 */
export default function WorkflowEditorPage({ workflowIdOverride }) {
  const params = useParams();
  const team = params.team;
  const workflowId = workflowIdOverride || params.workflowId;

  useHideGlobalTopBar(true);
  useBreadcrumb([]);

  const ed = useAgentEditor({ team, workflowId });
  const {
    isDraft, workflow, definition, openNode, openNodeId, previewVersion, problemByNode, error, setError,
  } = ed;

  const run = useRunPolling({
    workflowId, isDraft, definition, flushDraft: ed.flushDraft, setError, refreshWorkflow: ed.refreshWorkflow,
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null); // { fromId, port } | { edge } | null
  const [historyOpen, setHistoryOpen] = useState(false);

  const openPicker = useCallback((anchor) => { setPickerAnchor(anchor || null); setPickerOpen(true); }, []);
  const closePicker = useCallback(() => { setPickerOpen(false); setPickerAnchor(null); }, []);

  const pick = (kind) => {
    if (pickerAnchor?.edge) ed.addNodeOnEdge(kind, pickerAnchor.edge);
    else ed.addNode(kind, pickerAnchor);
    closePicker();
  };

  const onPreviewVersion = async (meta) => {
    const previewing = await ed.previewVersionOf(meta);
    if (previewing) { closePicker(); setHistoryOpen(false); }
  };

  const { handleRunNow, testActive, runningManual } = run;
  const { removeEdge } = ed;
  const actions = useMemo(() => ({
    onAddAfter: (fromId, port) => { if (!previewVersion) openPicker({ fromId, port: port ?? null }); },
    onInsertOnEdge: (edge) => { if (!previewVersion) openPicker({ edge }); },
    onRemoveEdge: removeEdge,
    onRunTrigger: () => { if (!testActive && !runningManual) handleRunNow(); },
  }), [previewVersion, openPicker, removeEdge, testActive, runningManual, handleRunNow]);

  const stepsShown = run.testVisible ? run.stepByNodeId : null;
  const panelOpen = !!openNode && !previewVersion;
  const triggerKind = ed.triggerNode?.kind || null;
  const triggerLabel = triggerKind ? (NODE_CATALOG[triggerKind]?.displayName || triggerKind) : 'manual';

  if (ed.loading) {
    return <div className="flex h-full items-center justify-center bg-surface"><BrandLoadingSpinner message="Loading agent…" /></div>;
  }
  if (!workflow) {
    return <div className="flex h-full items-center justify-center bg-surface text-[12.5px] text-[color:var(--text-muted)]">{error || 'Agent not found.'}</div>;
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-surface">
      <EditorTopBar
        team={team}
        workflowId={workflowId}
        isDraft={isDraft}
        workflow={workflow}
        nameDraft={ed.nameDraft}
        onNameChange={ed.setNameDraft}
        onNameBlur={ed.renameWorkflow}
        onSaveNew={ed.saveNewAgent}
        creating={ed.creating}
        previewVersion={previewVersion}
        onExitPreview={ed.exitVersionPreview}
        savingState={ed.savingState}
        savedFlash={ed.savedFlash}
        hasUnpublishedChanges={ed.hasUnpublishedChanges}
        historyOpen={historyOpen}
        onToggleHistory={() => setHistoryOpen((o) => !o)}
        testActive={run.testActive}
        runningManual={run.runningManual}
        hasNodes={definition.nodes.length > 0}
        onTest={run.handleRunNow}
        onStopTest={run.stopTest}
        publishing={ed.publishing}
        blocking={ed.blocking}
        onPublish={ed.publish}
      />

      <div className="flex min-h-0 flex-1">
        <div className="relative min-h-0 min-w-0 flex-1">
          <FlowCanvas
            definition={definition}
            stepByNodeId={stepsShown}
            mode="edit"
            selectedId={openNodeId}
            readOnly={!!previewVersion}
            onNodeClick={(_, n) => { if (!previewVersion) ed.setOpenNodeId(n.id); }}
            actions={actions}
            problemByNode={problemByNode}
            onConnect={ed.onConnect}
            onEdgesDelete={ed.onEdgesDelete}
            panelOpen={panelOpen}
            onAddStep={() => openPicker(null)}
          />

          {run.awaitingTrigger && (
            <div className="absolute left-1/2 top-3 z-30 flex -translate-x-1/2 items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-1.5 text-[11.5px] text-[color:var(--text-muted)] shadow-token-sm">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" />Awaiting {triggerLabel} trigger…
            </div>
          )}

          {!definition.nodes.length && !previewVersion && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="pointer-events-auto flex flex-col items-center gap-2 text-center">
                <button type="button" onClick={() => openPicker(null)} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] shadow-token-sm transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
                  Choose a trigger
                </button>
                <span className="text-[11px] text-[color:var(--text-muted)]">What starts this agent</span>
              </div>
            </div>
          )}

          {historyOpen && !previewVersion && (
            <VersionHistoryPanel
              workflowId={workflowId}
              publishedVersionId={workflow?.published_version_id}
              hasUnpublishedChanges={ed.hasUnpublishedChanges}
              previewVersionId={previewVersion?.id}
              onClose={() => setHistoryOpen(false)}
              onPreviewVersion={onPreviewVersion}
              onRepublished={ed.reloadAfterVersionChange}
              onRestored={ed.reloadAfterVersionChange}
            />
          )}
        </div>

        {panelOpen && (
          <NodeConfigPanel
            node={openNode}
            workflow={workflow}
            onChange={(patch) => ed.updateNode(openNode.id, patch)}
            onDelete={() => ed.deleteNode(openNode.id)}
            onClose={() => ed.setOpenNodeId(null)}
            definition={definition}
            testData={run.testData}
            liveStep={run.testData[openNode.id]}
            onRunTest={run.runTest}
            problem={problemByNode[openNode.id] || null}
          />
        )}
      </div>

      {pickerOpen && !previewVersion && (
        <NodePicker
          onClose={closePicker}
          onPick={pick}
          hasTrigger={!!ed.triggerNode}
          anchored={!!pickerAnchor}
        />
      )}

      {error && (
        <div role="alert" className="absolute bottom-16 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-3 py-1.5 text-[12px] text-danger shadow-token-sm">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss" className="rounded p-0.5 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
